import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BarChart3, Users, Calendar, CirclePlus } from 'lucide-react';
import { useLumisial } from '../context/LumisialContext';
import { aporteService, AporteAnalysis, AporteSummary, AporteMesSnapshot, AporteFilters, getDisplayPeriod } from '../services/aporteService';
import { Tabs, Tab } from '../components/ui/Tabs';
import { AportesIndicadores } from '../components/AportesIndicadores';
import { AportesListaMiembros } from '../components/AportesListaMiembros';
import { AportesHistoricoMensual } from '../components/AportesHistoricoMensual';
import { AporteModal } from '../components/AporteModal';
import { VolverButton } from '../components/ui/VolverButton';
import { leerPeriodo, rutaConPeriodo } from '../utils/periodoNavegacion';
import { useAuth } from '../context/AuthContext';
import { puedeOperarDineroDeLumisial } from '../utils/permissions';

export const DetalleAportesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedLumisial } = useLumisial();
  const { usuario } = useAuth();
  // Registrar, editar y anular dinero de este Lumisial: sus juntas locales y la excepción técnica global.
  const puedeOperar = puedeOperarDineroDeLumisial(usuario, selectedLumisial);
  const [activeTab, setActiveTab] = useState('indicadores');
  const [análisis, setAnálisis] = useState<AporteAnalysis[]>([]);
  const [resumen, setResumen] = useState<AporteSummary | null>(null);
  // Período: si viene en la dirección (se entró desde la tarjeta de Balance) manda ese; si no,
  // la regla propia de esta pantalla — mes en curso, salvo los primeros 5 días del mes, en que
  // muestra el anterior como período de gracia para registrar lo que cerró.
  const periodoInicial = useMemo(() => {
    const heredado = leerPeriodo(searchParams);
    // mes = 0 es "todo el año", que esta pantalla no sabe mostrar: cae a su regla propia.
    if (heredado && heredado.mes >= 1) return heredado;
    return getDisplayPeriod();
  }, [searchParams]);
  const [periodo] = useState(periodoInicial);
  const [historial, setHistorial] = useState<AporteMesSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAporteModal, setShowAporteModal] = useState(false);

  const tabs: Tab[] = [
    {
      id: 'indicadores',
      label: 'Resumen',
      icon: <BarChart3 className="w-5 h-5" />
    },
    {
      id: 'lista',
      label: 'Por Miembro',
      icon: <Users className="w-5 h-5" />
    },
    {
      id: 'historico',
      label: 'Histórico',
      icon: <Calendar className="w-5 h-5" />
    }
  ];

  useEffect(() => {
    if (selectedLumisial) {
      if (activeTab === 'indicadores') {
        loadResumen();
      } else if (activeTab === 'lista') {
        loadAnalisis({});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLumisial, activeTab, periodo]);

  const loadResumen = async () => {
    if (!selectedLumisial) return;

    try {
      setLoading(true);
      const displayPeriod = periodo;

      // 7 períodos: actual + 6 anteriores (para calcular flechas de los 6 mostrados)
      const periodos = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(displayPeriod.año, displayPeriod.mes - 1 - i, 1);
        return { mes: d.getMonth() + 1, año: d.getFullYear() };
      });

      const [resumenData, historialData] = await Promise.all([
        aporteService.getSummaryForMonth(selectedLumisial.id, displayPeriod.mes, displayPeriod.año),
        aporteService.getHistorialParticipacion(selectedLumisial.id, periodos)
      ]);

      setResumen(resumenData);
      setHistorial(historialData);
    } catch (error) {
      console.error('Error loading resumen:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalisis = async (filters: AporteFilters) => {
    if (!selectedLumisial) return;

    try {
      setLoading(true);
      // El período heredado alimenta las dos pestañas. Sin pasarlo acá, "Por Miembro" recalcula
      // el mes por su cuenta y muestra uno distinto al de "Resumen" en la misma pantalla.
      const [análisisData, resumenData] = await Promise.all([
        aporteService.getAnalysisByLumisial(selectedLumisial.id, filters, periodo),
        aporteService.getSummary(selectedLumisial.id, periodo)
      ]);
      setAnálisis(análisisData);
      setResumen(resumenData);
    } catch (error) {
      console.error('Error loading análisis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filters: AporteFilters) => {
    loadAnalisis(filters);
  };

  return (
    <div className="space-y-6">
      <VolverButton dataTour="volver-a-balance" onClick={() => { navigate(rutaConPeriodo('/lumisial/balance', searchParams.toString())); }} />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Aportes</h2>
          <p className="text-slate-600">Gestión completa de aportes y contribuciones de miembros</p>
        </div>
        {puedeOperar && (
        <button
          type="button"
          onClick={() => setShowAporteModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
        >
          <CirclePlus className="w-4 h-4" />
          Registrar Aporte
        </button>
        )}
      </div>

      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        variant="default"
      />

      <div className="mt-6">
        {activeTab === 'indicadores' && (
          <AportesIndicadores resumen={resumen} loading={loading} periodo={periodo} historial={historial} />
        )}

        {activeTab === 'lista' && selectedLumisial && (
          <AportesListaMiembros
            análisis={análisis}
            loading={loading}
            onFilterChange={handleFilterChange}
            lumisialId={selectedLumisial.id}
            puedeOperar={puedeOperar}
          />
        )}

        {activeTab === 'historico' && selectedLumisial && (
          <AportesHistoricoMensual lumisialId={selectedLumisial.id} añoInicial={periodo.año} puedeOperar={puedeOperar} />
        )}
      </div>
      <AporteModal
        isOpen={showAporteModal}
        onClose={() => setShowAporteModal(false)}
        onSuccess={() => {
          setShowAporteModal(false);
          if (activeTab === 'indicadores') loadResumen();
          else if (activeTab === 'lista') loadAnalisis({});
        }}
      />
    </div>
  );
};
